import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, finalize, map, Observable, switchMap, tap, Subject, takeUntil, distinctUntilChanged, catchError, of } from 'rxjs';
import { isEqual } from 'lodash-es';

import { StatisticsFilterPayload } from '../view/statistics-filter-bar/statistics-filter-bar.component';
import {
  FilterOption,
  MatchSearchFilterPayload,
  PaginatedSearchResultMatchSearchResultDTO,
  PaginatedSearchResultPlayerSearchResultDTO,
  PaginatedSearchResultTeamSearchResultDTO,
  PermissionsService,
  PlayerSearchFilterPayload,
  Season,
  StatisticsService,
  TeamSearchFilterPayload
} from "@solverml/api";

// Modelo local para los filtros de los selectores
export interface StatisticsFilters {
  seasons: Season[];
  competitions: FilterOption[];
  categories: FilterOption[];
  genders: FilterOption[];
}

// --- CONSTANTES PARA MANEJO DE ERRORES ---
const EMPTY_TEAM_RESULT: PaginatedSearchResultTeamSearchResultDTO = {
  total_results: 0, page: 1, page_size: 5, data: []
};
const EMPTY_MATCH_RESULT: PaginatedSearchResultMatchSearchResultDTO = {
  total_results: 0, page: 1, page_size: 10, data: []
};
const EMPTY_PLAYER_RESULT: PaginatedSearchResultPlayerSearchResultDTO = {
  total_results: 0, page: 1, page_size: 10, data: []
};

@Injectable({
  providedIn: 'root'
})
export class StatisticsStateService implements OnDestroy {

  // --- 1. ESTADO DE LOS FILTROS (Dropdowns) ---
  private filtersSubject = new BehaviorSubject<StatisticsFilters | null>(null);
  public filters$ = this.filtersSubject.asObservable();

  private isLoadingFiltersSubject = new BehaviorSubject<boolean>(true);
  public isLoadingFilters$ = this.isLoadingFiltersSubject.asObservable();

  // --- ESTADO DE LA BÚSQUEDA (Reactivo) ---
  private currentFiltersSubject = new BehaviorSubject<StatisticsFilterPayload | null>(null);
  private activeTabSubject = new BehaviorSubject<number>(0); // 0 = Equipos
  private statsModeSubject = new BehaviorSubject<'actas' | 'ia'>('actas');
  private destroy$ = new Subject<void>();

  // --- ESTADO DE LOS RESULTADOS (Pestañas) ---
  private teamsResultSubject = new BehaviorSubject<PaginatedSearchResultTeamSearchResultDTO | null>(null);
  public teamsResult$ = this.teamsResultSubject.asObservable();

  private matchesResultSubject = new BehaviorSubject<PaginatedSearchResultMatchSearchResultDTO | null>(null);
  public matchesResult$ = this.matchesResultSubject.asObservable();

  private playersResultSubject = new BehaviorSubject<PaginatedSearchResultPlayerSearchResultDTO | null>(null);
  public playersResult$ = this.playersResultSubject.asObservable();

  private isLoadingDataSubject = new BehaviorSubject<boolean>(false);
  public isLoadingData$ = this.isLoadingDataSubject.asObservable();

  // --- GESTIÓN DE ESTADO DEL BUSCADOR (SNAPSHOTS) ---
  private searchSnapshot = {
    selectedTabIndex: 0,
    statsMode: 'actas' as 'actas' | 'ia',
    matchFilters: null as any,
    teamFilters: null as any,
    playerFilters: null as any,
    teamPage: 1,
    matchPage: 1,
    playerPage: 1
  };

  constructor(
    private statsApi: StatisticsService,
    private permsApi: PermissionsService
  ) {
    this.setupReactiveSearchTrigger();
  }

  // ====================================================================
  //  MÉTODOS SNAPSHOT
  // ====================================================================

  public setSnapshotTabIndex(index: number) { this.searchSnapshot.selectedTabIndex = index; }
  public getSnapshotTabIndex(): number { return this.searchSnapshot.selectedTabIndex; }
  public setSnapshotStatsMode(mode: 'actas' | 'ia') { this.searchSnapshot.statsMode = mode; }
  public getSnapshotStatsMode(): 'actas' | 'ia' { return this.searchSnapshot.statsMode; }
  public setStatsMode(mode: 'actas' | 'ia') { this.statsModeSubject.next(mode); }
  public setMatchFiltersSnapshot(filters: any) { this.searchSnapshot.matchFilters = filters; }
  public getMatchFiltersSnapshot() { return this.searchSnapshot.matchFilters; }
  public setTeamFiltersSnapshot(filters: any) { this.searchSnapshot.teamFilters = filters; }
  public getTeamFiltersSnapshot() { return this.searchSnapshot.teamFilters; }
  public setPlayerFiltersSnapshot(filters: any) { this.searchSnapshot.playerFilters = filters; }
  public getPlayerFiltersSnapshot() { return this.searchSnapshot.playerFilters; }
  public getCurrentFiltersSnapshot(): StatisticsFilterPayload | null { return this.currentFiltersSubject.value; }

  public clearSearchSnapshot() {
    this.searchSnapshot = {
      selectedTabIndex: 0,
      statsMode: 'actas',
      matchFilters: null,
      teamFilters: null,
      playerFilters: null,
      teamPage: 1,
      matchPage: 1,
      playerPage: 1
    };
  }

  // ====================================================================
  //  LIFECYCLE & RESET
  // ====================================================================

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public resetState(): void {
    this.filtersSubject.next(null);
    this.isLoadingFiltersSubject.next(true);
    this.currentFiltersSubject.next(null);
    this.activeTabSubject.next(0);
    this.teamsResultSubject.next(null);
    this.matchesResultSubject.next(null);
    this.playersResultSubject.next(null);
    this.isLoadingDataSubject.next(false);
    this.statsModeSubject.next('actas');
  }

  // ====================================================================
  //  CONFIGURACIÓN Y SETTERS
  // ====================================================================

  public loadInitialFilters(): void {
    this.isLoadingFiltersSubject.next(true);
    combineLatest({
      seasons: this.statsApi.getSeasonsEndpoint(),
      filters: this.permsApi.getVideoPermissionFilters()
    }).pipe(
      map(({ seasons, filters }) => {
        const GENDER_ID_MAP: { [key: string]: number } = {
          'MASCULINO': 0, 'FEMENINO': 1, 'MIXTO': 2, 'NO ESPECIFICADO': 3
        };
        const correctedGenders = filters.genders.map(g => ({
          name: g.name,
          id: GENDER_ID_MAP[g.name] !== undefined ? GENDER_ID_MAP[g.name] : g.id
        }));
        return {
          seasons: seasons,
          competitions: filters.competitions,
          categories: filters.categories,
          genders: correctedGenders
        };
      }),
      finalize(() => this.isLoadingFiltersSubject.next(false)),
      catchError(err => {
        console.error("Error loading initial filters", err);
        return of(null);
      })
    ).subscribe(allFilters => {
      if (allFilters) {
        this.filtersSubject.next(allFilters);
      }
    });
  }

  public setFilters(payload: StatisticsFilterPayload): void {
    const oldFilters = this.currentFiltersSubject.value;
    if (oldFilters && !isEqual(oldFilters, payload)) {
      this.searchSnapshot.teamPage = 1;
      this.searchSnapshot.matchPage = 1;
      this.searchSnapshot.playerPage = 1;
    }
    this.currentFiltersSubject.next(payload);
  }

  public setActiveTab(tabIndex: number): void {
    this.activeTabSubject.next(tabIndex);
  }

  // ====================================================================
  //  LÓGICA DE BÚSQUEDA REACTIVA
  // ====================================================================

  private setupReactiveSearchTrigger(): void {
    combineLatest([
      this.currentFiltersSubject,
      this.activeTabSubject,
      this.statsModeSubject
    ]).pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),

      tap(([filters, _tab, mode]) => {
        if (!!filters && (mode === 'ia' || filters.seasonId != null)) {
          this.isLoadingDataSubject.next(true);
        }
      }),

      switchMap(([filters, tab, mode]) => {
        if (!filters) {
          this.teamsResultSubject.next(null);
          this.matchesResultSubject.next(null);
          this.playersResultSubject.next(null);
          this.isLoadingDataSubject.next(false);
          return of(null);
        }

        if (mode === 'actas' && filters.seasonId == null) {
          this.teamsResultSubject.next(null);
          this.matchesResultSubject.next(null);
          this.playersResultSubject.next(null);
          this.isLoadingDataSubject.next(false);
          return of(null);
        }

        if (mode === 'ia' && tab !== 0) {
          this.matchesResultSubject.next(null);
          this.playersResultSubject.next(null);
          this.isLoadingDataSubject.next(false);
          return of(null);
        }

        this.clearOtherTabResults(tab);

        switch (tab) {
          case 0: return this.fetchTeams(filters);
          case 1: return this.fetchMatches(filters);
          case 2: return this.fetchPlayers(filters);
          default: return of(null);
        }
      })
    ).subscribe();
  }

  private fetchTeams(filters: StatisticsFilterPayload): Observable<any> {
    const payload: TeamSearchFilterPayload & { is_ia_search?: boolean } = {
      competition_ids: filters.competitionIds,
      genders: filters.gender != null ? [filters.gender] : undefined,
      text_search: filters.textSearch || undefined,
      page: this.searchSnapshot.teamPage,
      page_size: 10,
      is_ia_search: this.statsModeSubject.value === 'ia'
    };
    if (filters.seasonId != null) {
      payload.season_id = filters.seasonId;
    }

    this.isLoadingDataSubject.next(true);

    return this.statsApi.searchTeamsEndpoint(payload as TeamSearchFilterPayload).pipe(
      tap(results => this.teamsResultSubject.next(results)),
      catchError(err => {
        console.error("Error fetching teams", err);
        this.teamsResultSubject.next(EMPTY_TEAM_RESULT);
        return of(null);
      }),
      finalize(() => this.isLoadingDataSubject.next(false))
    );
  }

  private fetchMatches(filters: StatisticsFilterPayload): Observable<any> {
    const basePayload: MatchSearchFilterPayload = {
      season_id: filters.seasonId!,
      competition_ids: filters.competitionIds,
      genders: filters.gender != null ? [filters.gender] : undefined,
      text_search: filters.textSearch || undefined,
      page: this.searchSnapshot.matchPage,
      page_size: 10
    };

    this.isLoadingDataSubject.next(true);

    return this.statsApi.searchMatchesEndpoint({ ...basePayload, page_size: 1 }).pipe(
      switchMap(initialResult => {
        const total = initialResult.total_results;
        const lastPage = total > 0 ? Math.ceil(total / basePayload.page_size) : 1;
        return this.statsApi.searchMatchesEndpoint({ ...basePayload, page: lastPage });
      }),
      tap(results => this.matchesResultSubject.next(results)),
      catchError(err => {
        console.error("Error fetching matches", err);
        this.matchesResultSubject.next(EMPTY_MATCH_RESULT);
        return of(null);
      }),
      finalize(() => this.isLoadingDataSubject.next(false))
    );
  }

  private fetchPlayers(filters: StatisticsFilterPayload): Observable<any> {
    const payload: PlayerSearchFilterPayload = {
      season_id: filters.seasonId!,
      competition_ids: filters.competitionIds,
      genders: filters.gender != null ? [filters.gender] : undefined,
      player_name: filters.textSearch || undefined,
      page: this.searchSnapshot.playerPage,
      page_size: 10
    };

    this.isLoadingDataSubject.next(true);

    return this.statsApi.searchPlayersEndpoint(payload).pipe(
      tap(results => this.playersResultSubject.next(results)),
      catchError(err => {
        console.error("Error fetching players", err);
        this.playersResultSubject.next(EMPTY_PLAYER_RESULT);
        return of(null);
      }),
      finalize(() => this.isLoadingDataSubject.next(false))
    );
  }

  // ====================================================================
  // MÉTODOS DE PAGINACIÓN
  // ====================================================================

  public paginateTeams(page: number): void {
    const filters = this.currentFiltersSubject.value;
    if (!filters) return;
    
    this.searchSnapshot.teamPage = page;

    const payload: TeamSearchFilterPayload & { is_ia_search?: boolean } = {
      competition_ids: filters.competitionIds,
      genders: filters.gender != null ? [filters.gender] : undefined,
      text_search: filters.textSearch || undefined,
      page: page,
      page_size: 10,
      is_ia_search: this.statsModeSubject.value === 'ia'
    };
    if (filters.seasonId != null) {
      payload.season_id = filters.seasonId;
    }

    this.isLoadingDataSubject.next(true);

    this.statsApi.searchTeamsEndpoint(payload as TeamSearchFilterPayload).pipe(
      tap(results => this.teamsResultSubject.next(results)),
      catchError(err => {
        this.teamsResultSubject.next(EMPTY_TEAM_RESULT);
        return of(null);
      }),
      finalize(() => this.isLoadingDataSubject.next(false))
    ).subscribe();
  }

  public paginateMatches(page: number): void {
    const filters = this.currentFiltersSubject.value;
    if (!filters) return;
    
    this.searchSnapshot.matchPage = page;

    const payload: MatchSearchFilterPayload = {
      season_id: filters.seasonId!,
      competition_ids: filters.competitionIds,
      genders: filters.gender != null ? [filters.gender] : undefined,
      text_search: filters.textSearch || undefined,
      page: page,
      page_size: 10
    };

    this.isLoadingDataSubject.next(true);

    this.statsApi.searchMatchesEndpoint(payload).pipe(
      tap(results => this.matchesResultSubject.next(results)),
      catchError(err => {
        this.matchesResultSubject.next(EMPTY_MATCH_RESULT);
        return of(null);
      }),
      finalize(() => this.isLoadingDataSubject.next(false))
    ).subscribe();
  }

  public paginatePlayers(page: number): void {
    const filters = this.currentFiltersSubject.value;
    if (!filters) return;

    this.searchSnapshot.playerPage = page;

    const payload: PlayerSearchFilterPayload = {
      season_id: filters.seasonId!,
      competition_ids: filters.competitionIds,
      genders: filters.gender != null ? [filters.gender] : undefined,
      player_name: filters.textSearch || undefined,
      page: page,
      page_size: 10
    };

    this.isLoadingDataSubject.next(true);

    this.statsApi.searchPlayersEndpoint(payload).pipe(
      tap(results => this.playersResultSubject.next(results)),
      catchError(err => {
        this.playersResultSubject.next(EMPTY_PLAYER_RESULT);
        return of(null);
      }),
      finalize(() => this.isLoadingDataSubject.next(false))
    ).subscribe();
  }

  private clearOtherTabResults(activeTab: number): void {
    if (activeTab !== 0) this.teamsResultSubject.next(null);
    if (activeTab !== 1) this.matchesResultSubject.next(null);
    if (activeTab !== 2) this.playersResultSubject.next(null);
  }
}
